import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import {
  downloadPayrollSlipPdf,
  getPayrollItemSlip,
} from "../services/PayrollService";
import type { PayrollSlip } from "../types/payroll.types";
import { formatCurrency } from "utils/currency";

const PayslipDetailModal = ({
  isOpen,
  onClose,
  itemId,
}: {
  isOpen: boolean;
  onClose: () => void;
  itemId: number | null;
}) => {
  const [loading, setLoading] = useState(false);
  const [slip, setSlip] = useState<PayrollSlip | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen || itemId == null) {
      setSlip(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPayrollItemSlip(itemId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setSlip(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, itemId]);

  const handleDownload = async () => {
    if (!slip) return;
    setDownloading(true);
    try {
      const blob = await downloadPayrollSlipPdf(slip.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-${slip.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const d = slip?.data;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Payslip</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : !d ? (
          <div className="text-center py-4 text-muted">
            No payslip data available.
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
              <div>
                <h6 className="mb-1">{d.employee.name}</h6>
                {d.employee.email && (
                  <div className="text-muted small">{d.employee.email}</div>
                )}
                {d.employee.department && (
                  <div className="text-muted small">{d.employee.department}</div>
                )}
                {d.employee.designation && (
                  <div className="text-muted small">
                    {d.employee.designation}
                  </div>
                )}
              </div>
              <div className="text-end">
                <div className="fw-medium">
                  {new Date(d.period.year, d.period.month - 1).toLocaleString(
                    "default",
                    { month: "long", year: "numeric" },
                  )}
                </div>
                {d.payrollRunCode && (
                  <div className="text-muted small">Run {d.payrollRunCode}</div>
                )}
                {d.status && (
                  <Badge color="success" className="mt-1">
                    {d.status}
                  </Badge>
                )}
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <h6 className="text-muted text-uppercase fs-11 mb-2">
                  Earnings
                </h6>
                <Table size="sm" bordered className="mb-0">
                  <tbody>
                    {d.earnings.map((e, i) => (
                      <tr key={i}>
                        <td>{e.label ?? e.type}</td>
                        <td className="text-end tabular-nums">
                          {formatCurrency(e.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="table-light fw-semibold">
                      <td>Gross</td>
                      <td className="text-end tabular-nums">
                        {formatCurrency(d.grossAmount)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <h6 className="text-muted text-uppercase fs-11 mb-2">
                  Deductions
                </h6>
                <Table size="sm" bordered className="mb-0">
                  <tbody>
                    {d.deductions.length > 0 ? (
                      d.deductions.map((e, i) => (
                        <tr key={i}>
                          <td>{e.label ?? e.type}</td>
                          <td className="text-end tabular-nums">
                            {formatCurrency(e.amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-muted text-center">
                          No deductions
                        </td>
                      </tr>
                    )}
                    <tr className="table-light fw-semibold">
                      <td>Total deductions</td>
                      <td className="text-end tabular-nums">
                        {formatCurrency(d.totalDeductions)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="p-3 bg-success-subtle rounded text-end">
              <span className="text-muted me-2">Net pay:</span>
              <span className="fs-5 fw-bold text-success">
                {formatCurrency(d.netAmount)}
              </span>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          Close
        </Button>
        {slip && (
          <Button color="primary" disabled={downloading} onClick={handleDownload}>
            {downloading ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-download-2-line me-1" />
            )}
            Download PDF
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PayslipDetailModal;
